/**
 * チャートコンテナの実測幅 (px)。X 軸ラベルの間引き判定に使う
 * (`models/chartAxis` の `resolveXAxisLabelStepHours`)。
 *
 * 高さ側の `useForecastChartHeight` と同じ観測手段 (ResizeObserver + window
 * resize) を使うが、**責務が別**なので hook を分ける — 高さはスクロール祖先や
 * カード上端まで辿って「ビューポートにどれだけ残っているか」を出す計算で、
 * 幅は自要素 1 個の測定で済む。合流させると高さ側の重い探索が幅の再計算にも
 * 走る。
 *
 * 幅が測れない環境 (レイアウトエンジンを持たない happy-dom = テスト、初回描画)
 * では **0 を返す**。呼び出し側 (`resolveXAxisLabelStepHours`) が 0 以下を
 * 「間引かない」に倒すので、刻みが勝手に粗くなることはない。
 */
import type { RefObject } from 'react'
import { useLayoutEffect, useState } from 'react'

/** resize の連打を束ねる間隔 (ms)。`useForecastChartHeight` と同値。 */
const RESIZE_DEBOUNCE_MS = 150

export function useForecastChartWidth(
  containerRef: RefObject<HTMLElement | null>,
): number {
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (container === null || typeof window === 'undefined') return

    const measure = (): void => {
      setWidth(container.getBoundingClientRect().width)
    }
    measure()

    let resizeTimer: ReturnType<typeof setTimeout> | undefined
    const handleResize = (): void => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(measure, RESIZE_DEBOUNCE_MS)
    }
    window.addEventListener('resize', handleResize)

    // 2 カラム ⇄ 1 カラムの切替はコンテナ自身の幅変化として出るので、
    // 自要素を観測すれば足りる (高さ側のようにスクロール祖先を辿る必要は無い)。
    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(container)
    }

    return () => {
      clearTimeout(resizeTimer)
      window.removeEventListener('resize', handleResize)
      resizeObserver?.disconnect()
    }
  }, [containerRef])

  return width
}
